import { useCallback, useMemo } from 'react';
import useUserFeaturesStore from '../store/userFeaturesStore';
import { FEATURE_CODES, PUBLIC_UI_ELEMENTS, getFeatureByElement } from '../constants/featureElements';

/**
 * Hook kiểm tra quyền truy cập feature
 * KHÔNG gọi API - chỉ đọc state từ store
 * API được gọi bởi FeatureProvider khi user đăng nhập
 * @returns {Object} - Các hàm và state liên quan đến feature access
 */
const useFeatureAccess = () => {
  const {
    subscription,
    features,
    uiElements,
    loading,
    error,
    initialized,
    fetchUserFeatures,
    hasFeature,
    getFeature,
  } = useUserFeaturesStore();

  /**
   * Kiểm tra user có quyền xem element không
   * @param {string} elementName - Tên component/element
   * @returns {boolean}
   */
  const canAccessElement = useCallback((elementName) => {
    // Public elements luôn hiển thị
    if (PUBLIC_UI_ELEMENTS.includes(elementName)) {
      return true;
    }

    // Nếu đang loading hoặc chưa có data, cho phép tạm thời
    if (loading || uiElements.length === 0) {
      return true;
    }

    // Kiểm tra element có trong danh sách được phép không
    return uiElements.includes(elementName);
  }, [uiElements, loading]);

  /**
   * Kiểm tra user có quyền truy cập feature không
   * @param {string} featureCode - Code của feature
   * @returns {boolean}
   */
  const canAccessFeature = useCallback((featureCode) => {
    // Dashboard và Plans luôn accessible
    if (featureCode === FEATURE_CODES.DASHBOARD || featureCode === FEATURE_CODES.PLANS) {
      return true;
    }

    if (loading || features.length === 0) {
      return true;
    }

    return hasFeature(featureCode);
  }, [features, loading, hasFeature]);

  /**
   * Lấy giới hạn usage của feature
   * @param {string} featureCode - Code của feature
   * @returns {Object|null} - { limit_usage, limit_period }
   */
  const getFeatureLimit = useCallback((featureCode) => {
    const feature = getFeature(featureCode);
    if (!feature) return null;

    return {
      limitUsage: feature.limit_usage,
      limitPeriod: feature.limit_period,
      isUnlimited: feature.limit_usage === null,
    };
  }, [getFeature]);

  /**
   * Lấy feature code từ element name
   * @param {string} elementName - Tên component/element
   * @returns {string|null}
   */
  const getElementFeature = useCallback((elementName) => {
    return getFeatureByElement(elementName);
  }, []);

  // Memo các giá trị để tránh re-render không cần thiết
  const isLoaded = useMemo(() => initialized && !loading, [initialized, loading]);
  const hasSubscription = useMemo(() => subscription !== null, [subscription]);

  return {
    // State
    subscription,
    features,
    uiElements,
    loading,
    error,
    initialized,
    isLoaded,
    hasSubscription,

    // Actions
    fetchUserFeatures,
    canAccessElement,
    canAccessFeature,
    getFeatureLimit,
    getElementFeature,
    hasFeature,
    getFeature,
  };
};

export default useFeatureAccess;

